import { useCallback, useRef, useState } from 'react';

type Updater<T> = T | ((prev: T) => T);

interface ControllableStateProps<T> {
    value?: T;
    defaultValue: T;
    onChange?: (v: T) => void;
}

export const useControllableState = <T>({ value, defaultValue, onChange }: ControllableStateProps<T>) => {
    const isControlled = value !== undefined;
    const [internal, setInternal] = useState<T>(defaultValue);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const state = (isControlled ? value : internal) as T;

    const set = useCallback(
        (next: Updater<T>) => {
            const computed = typeof next === 'function' ? (next as (p: T) => T)(state) : next;

            if (!isControlled) setInternal(computed);
            onChangeRef.current?.(computed);
        },
        [isControlled, state],
    );

    return [state, set, isControlled] as const;
};
